/**
 * Claude Code 权限管理工具
 *
 * 确保 workflow-cli 的所有功能都有正确的权限配置
 */

export interface ClaudePermissions {
  allow: string[];
  deny?: string[];
}

/**
 * 获取 workflow-cli 需要的基础权限
 */
export function getBasePermissions(): string[] {
  return [
    // 基础命令
    'Bash(cd:*)',
    'Bash(pwd)',
    'Bash(ls:*)',
    'Bash(cat:*)',
    'Bash(echo:*)',

    // npm/npx 相关
    'Bash(npm:*)',
    'Bash(npx:*)',
    'Bash(pnpm:*)',

    // workflow-cli 命令
    'Bash(workflow:*)',
    'Bash(wf:*)',
    'Bash(npx @gongfu/workflow-cli:*)',

    // Git 操作
    'Bash(git status)',
    'Bash(git add:*)',
    'Bash(git commit:*)',
    'Bash(git log:*)',

    // 文件操作
    'Read(**)',
    'Write(**)',
    'Edit(**)',
    'MultiEdit(**)',

    // Todo 操作
    'TodoRead',
    'TodoWrite',

    // 测试和构建
    'Bash(npm test:*)',
    'Bash(npm run:*)',
    'Bash(pnpm test:*)',
    'Bash(pnpm run:*)',

    // 进程管理
    'Bash(ps:*)',
    'Bash(kill:*)',
  ];
}

/**
 * 获取 orchestrate 功能需要的权限
 */
export function getOrchestratePermissions(): string[] {
  return [
    // orchestrate 命令
    'Bash(**/workflow-cli orchestrate:*)',
    'Bash(**/workflow-cli orch:*)',
    'Bash(npx @gongfu/workflow-cli orchestrate:*)',

    // Hook 执行需要的权限
    'Bash(cd "*" && npx @gongfu/workflow-cli orchestrate:*)',

    // 文件搜索（用于查找完成标记）
    'Bash(grep:*)',
    'Bash(find:*)',

    // 任务管理
    'Bash(**/workflow-cli task:*)',
    'Bash(npx @gongfu/workflow-cli task:*)',
  ];
}

/**
 * 获取团队协作需要的权限
 */
export function getTeamPermissions(): string[] {
  return [
    'Bash(**/workflow-cli team:*)',
    'Bash(npx @gongfu/workflow-cli team:*)',
    'Bash(**/workflow-cli notify:*)',
    'Bash(npx @gongfu/workflow-cli notify:*)',
  ];
}

/**
 * 合并权限列表，去重
 */
export function mergePermissions(...permissionLists: string[][]): string[] {
  const allPermissions = new Set<string>();

  permissionLists.forEach(list => {
    list.forEach(perm => allPermissions.add(perm));
  });

  return Array.from(allPermissions).sort();
}

/**
 * 检查权限是否已存在
 */
export function hasPermission(permissions: ClaudePermissions, permission: string): boolean {
  return permissions.allow.some(p => {
    // 精确匹配
    if (p === permission) return true;

    // 通配符匹配
    if (p.includes('*') || permission.includes('*')) {
      // 将权限模式转换为正则表达式
      const escapeRegex = (str: string) => str.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

      // 处理 p 的通配符
      let pPattern = escapeRegex(p).replace(/\\\*/g, '.*');
      let permPattern = escapeRegex(permission).replace(/\\\*/g, '.*');

      // 双向检查：p 匹配 permission 或 permission 匹配 p
      const pRegex = new RegExp(`^${pPattern}$`);
      const permRegex = new RegExp(`^${permPattern}$`);

      return pRegex.test(permission) || permRegex.test(p);
    }

    return false;
  });
}

/**
 * 添加权限到配置
 */
export function addPermissions(
  currentPermissions: ClaudePermissions,
  newPermissions: string[]
): ClaudePermissions {
  const updatedAllow = [...currentPermissions.allow];

  newPermissions.forEach(perm => {
    if (!hasPermission(currentPermissions, perm)) {
      updatedAllow.push(perm);
    }
  });

  return {
    ...currentPermissions,
    allow: updatedAllow.sort(),
  };
}

/**
 * 获取完整的 workflow-cli 权限集
 */
export function getFullWorkflowPermissions(): string[] {
  return mergePermissions(getBasePermissions(), getOrchestratePermissions(), getTeamPermissions());
}
